import {MmFaultRightPanePageEnum} from './mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.enum';
import {MmFaultRightPaneService} from './mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.service';
import {MmFaultChangePageService} from './mm-faults-view/mm-fault-right-pane/mm-fault-change-page.service';
import {MmPlanEditService} from './mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmPlansManagerService} from './mm-plans-view/services/mm-plans-manager.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {EventEmitter, Injectable} from '@angular/core';
import {first, firstValueFrom} from 'rxjs';
import {filter} from 'rxjs/operators';
import {MmFaultsViewService} from './mm-faults-view/services/mm-faults-view.service';
import {MmFaultsLoaderService} from './mm-faults-loader.service';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {ConnectivityService} from '@mydocma/core';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {MmFaultsSplitService} from './mm-faults-view/services/mm-faults-split.service';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {AppContainerService} from '@mydocma/app-container';
import {PlanEditModeEnum} from './mm-plans-view/mm-plan-view/services/plan-edit-mode.enum';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {MmProjectPermissionsService} from '../mm-project/mm-project-permissions-service';
import {MmFaultPanePageComponent} from './mm-faults-view/mm-fault-right-pane/pages/mm-fault-pane-page/mm-fault-pane-page.component';
import {MmFaultDetailsService} from './mm-fault/mm-fault-detail-form/services/mm-fault-detail.service';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {FaultDuplicateWizardComponent} from './fault-duplicate-wizard/fault-duplicate-wizard.component';
import {FaultDuplicateWizardViewService} from './fault-duplicate-wizard/services/fault-duplicate-wizard.view.service';
import {FaultDuplicateWizardStepperService} from './fault-duplicate-wizard/services/fault-duplicate-wizard-stepper.service';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {Fault} from '@docma-mm-core/fault/fault';
import { FaultPersistenceService } from '@docma-mm-core/fault/fault-persistence.service';
import {Logger} from '@mydocma/common/src/lib/logger';

@Injectable()
export class MmFaultActionsService {

  private static readonly FAULT_LOAD_TIMEOUT_MS = 3000;

  constructor(private readonly rightPaneService: MmFaultRightPaneService,
              private readonly changeService: MmFaultChangePageService,
              private readonly planEditService: MmPlanEditService,
              private readonly plansManagerService: MmPlansManagerService,
              private readonly faultsViewService: MmFaultsViewService,
              private readonly faultsLoaderService: MmFaultsLoaderService,
              private readonly deviceService: DeviceService,
              private readonly connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly translateService: TranslateService,
              private readonly notificationService: NotificationService,
              readonly faultsSplitService: MmFaultsSplitService,
              private readonly projectLoaderService: MmProjectLoaderService,
              public appContainerService: AppContainerService,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly faultDetailService: MmFaultDetailsService,
              private readonly dialog: MatDialog,
              private readonly duplicateViewService: FaultDuplicateWizardViewService,
              private readonly duplicateStepperService: FaultDuplicateWizardStepperService,
              private readonly faultService: FaultService,
              private readonly faultPersistenceService: FaultPersistenceService,
              private readonly logger: Logger) {

  }

  permissinEditPin$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditPins]);
  permissionAddAttachment$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.AddAttachment]);

  public closePlanView = new EventEmitter();

  executingComponent: any;

  async closeByPlanViewer(): Promise<boolean> {
    let closePage = false;
    const currentFault = await firstValueFrom(this.faultsViewService.currentFault);


    const hasPendingChanges =
        !!this.changeService.hasChanges(MmFaultRightPanePageEnum.plan) ||
        !!this.changeService.hasChanges(MmFaultRightPanePageEnum.fault);

    if ((this.deviceService.isMobil() || this.deviceService.isTablet()) && !!currentFault?.unsavedNew && hasPendingChanges) {
      const isDvOpened = this.rightPaneService.pagesPath.length > 0;
      if (!isDvOpened) {
        if (await this.rightPaneService.promtDiscardChanges()) {
          closePage = await this.onClose(currentFault);
        }
      }
      else {
        closePage = await this.onClose(currentFault);
      }
      closePage = false;
    }
    else {
      const planEditMode = await firstValueFrom(this.planEditService.planEditMode$);
      if (planEditMode !== PlanEditModeEnum.None && hasPendingChanges) {
        if (await this.rightPaneService.promtDiscardChanges()) {
          closePage = true;
        }
      }
      else {
        closePage = true;
      }
    }

    if (closePage) {
      if (this.planEditService.planEditMode !== PlanEditModeEnum.None) {
        this.closePlanView.emit();
        this.planEditService.closeEditPin();
        this.changeService.disableChanges(MmFaultRightPanePageEnum.plan);
        this.plansManagerService.resetFaultStructure();

        if (this.plansManagerService.selectedPlan === null) {
          this.plansManagerService.setSelectedPlan(this.plansManagerService.plansByStructure[0]);
          this.plansManagerService.setPlanFlyoutVisible(false);
        }
      } else {
        this.closePlanView.emit();
        this.changeService.disableChanges(MmFaultRightPanePageEnum.plan);
        this.faultsSplitService.disablePlanView();
      }

      if (!!this.executingComponent && this.executingComponent instanceof MmFaultPanePageComponent) {
        this.faultsViewService.openRightPane(currentFault);
      }
    }

    delete this.executingComponent;
    return closePage;
  }


  async onClose(currentFault?: ViewFault) {
    if (!this.deviceService.isDesktop()) { this.faultDetailService.preventTempFormFocusForIos = true; }
    const pageClosed = await this.rightPaneService.closePanePage(false, true);

    if (pageClosed) {
      this.planEditService.closeEditPin();
      if (this.plansManagerService.selectedPlan === null) {
        const plansByStructure = this.plansManagerService.plansByStructure;
        await this.plansManagerService.setSelectedPlan(plansByStructure[0]);
      }

      if (await this.plansManagerService.selectedPlan === null) {
        await this.plansManagerService.setSelectedPlan(this.plansManagerService.allPlans[0]);
      }
      this.changeService.disableChanges(MmFaultRightPanePageEnum.fault);
      this.changeService.disableChanges(MmFaultRightPanePageEnum.plan);

      if (this.deviceService.isMobil() || this.deviceService.isTablet()) {
        if (!currentFault) {
          currentFault = await firstValueFrom(this.faultsViewService.currentFault);
        }

        if (currentFault && !!currentFault?.unsavedNew) {
          const persistenceFault = await this.faultPersistenceService.get(currentFault?.project?.MyDocmaUid, currentFault.uid);
          if(!persistenceFault){
            this.faultsLoaderService.removeFaults([currentFault]);
            this.faultsViewService.setCurrentFault(null);
          }

          // this.faultDetailsService.discardChanges();
          // this.planEditService.setPlanEditMode(PlanEditModeEnum.None);
        }
      }
    }
    setTimeout(() => this.faultDetailService.preventTempFormFocusForIos = false, 50);
    return pageClosed;
  }

  async onDeleteClick(faults: ViewFault[]) {
    if (await this.connectivityService.appIsInOfflineMode.pipe(first()).toPromise() === true) {
      this.logger.error('MM.dialog.onlyInOnline', '', 'MmFaultActionsService');
      this.notificationService.error(await this.translateService.get('MM.dialog.onlyInOnline').toPromise());
      return;
    }

    const title = await this.translateService.get('MM.dialog.deleteFault.title').toPromise();
    const content = await this.translateService.get('MM.dialog.deleteFault.content').toPromise();

    if ((await this.dialogService.showDeleteConfirmationDialog({title, content}))?.value && await this.faultsViewService.deleteFaults(faults)) {
      await this.rightPaneService.closePanePage(false, false);
      this.changeService.disableChanges(MmFaultRightPanePageEnum.fault);
      this.changeService.disableChanges(MmFaultRightPanePageEnum.plan);
    }
  }

  async onDuplicateClick(fault: ViewFault) {
    const project = await firstValueFrom(this.projectLoaderService.project);

    if (fault && fault.data && fault.data.Structure && !this.connectivityService.isOfflineVal) {
      this.ensureFaultLoadedForDuplication(fault, project.data.MyDocmaUid);
    }

    const dialogRef = this.dialog.open(FaultDuplicateWizardComponent, {
      data: { fault, project: project.data },
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-fault-duplicate-dialog'],
      autoFocus: false,
      disableClose: true,
      width: '800px',
      maxWidth: '90vw'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result && result.shouldExecute) {
      try {
        const success = await this.duplicateViewService.executeDuplication();

        if (!success) {
          this.logger.error('Fault duplication failed', '', 'MmFaultActionsService');
        }
      } catch (error) {
        this.logger.error(error, '', 'MmFaultActionsService');
        this.notificationService.error(
          this.translateService.instant('MM.fault.duplicate.error.unexpected')
        );
      }
    } else {
      this.duplicateStepperService.reset();
    }
  }

  private async ensureFaultLoadedForDuplication(fault: ViewFault, mydocmaUid: string): Promise<void> {
    if (!fault?.id || !fault?.data?.Structure?.Id) {
      return;
    }

    const loaded$ = this.faultService.onFaultLoaded.pipe(filter(f => f?.uid === fault.uid));

    try {
      await this.faultService.loadFaults(
        mydocmaUid,
        fault.data.Structure.Id,
        [fault as unknown as ViewFault],
        true
      );

      await Promise.race([
        firstValueFrom(loaded$),
        new Promise(resolve => setTimeout(resolve, MmFaultActionsService.FAULT_LOAD_TIMEOUT_MS))
      ]);
    } catch {}
  }


  async onLocatePinOnPlan(fault: ViewFault, executingComponent: any) {

    if (this.faultsSplitService.toggledRightViews.includes('planView')) {
      if (this.plansManagerService.selectedPlan == null) {
        this.dialogService.showNotificationDialog({
          title: this.translateService.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateService.instant('MM.plan.error.noPlansOnStructure'),
          content: this.plansManagerService.allPlans.length == 0 ? this.translateService.instant('MM.plan.error.noPlansInProject')
            : this.translateService.instant('MM.plan.error.newFaultNoPlans.changeStructure')
        });
        return;
      }
      if (!(await this.plansManagerService.checkFaultInPlanStructure(fault?.data?.Structure.Id, this.plansManagerService.selectedPlan.data.Structure.Id))) {
        this.dialogService.showNotificationDialog({
          title: this.translateService.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateService.instant('MM.plan.error.faultPlanStructureInvalid'),
          content: this.translateService.instant('MM.plan.error.selectAnotherPlan'),
        });
        return;
      }
    } else {
      const editPlanList = await this.planEditService.filterPlansEditList(fault?.data?.Structure);
      if (editPlanList?.length === 0) {
        if (this.plansManagerService.allPlans.length > 0) {
          this.dialogService.showNotificationDialog({
            title: this.translateService.instant('MM.plan.error.pinNotPossible'),
            subTitle: this.translateService.instant('MM.plan.error.noPlansOnStructure'),
            content: this.translateService.instant('MM.plan.error.pinFaultNoPlans.changeFaultStructure')
          });
          return;
        } else {
          this.dialogService.showNotificationDialog({
            title: this.translateService.instant('MM.plan.error.pinNotPossible'),
            content: this.translateService.instant('MM.plan.error.noPlansInProject')
          });
          return;
        }
      } else if (editPlanList.length === 1) {
        await this.plansManagerService.setSelectedPlan(editPlanList[0]);
      } else {
        await this.plansManagerService.setSelectedPlan(null);
      }
    }

    this.executingComponent = executingComponent;

    await this.faultsViewService.setCurrentFault(fault);
    await this.planEditService.addFaultOnPin();
  }

  async onPlanClipping(fault: ViewFault, executingComponent: any) {

    await this.faultsSplitService.setRightViewVisble(true);
    const plan = this.plansManagerService.allPlans.find(p => p.id === fault.pin.FileId);
    await this.plansManagerService.setSelectedPlan(plan);

    const editPinsPermission = await firstValueFrom(this.permissinEditPin$.pipe(first()));

    this.executingComponent = executingComponent;

    this.planEditService.isPlanEditInitializingSubject.next(true);

    await this.faultsViewService.setCurrentFault(fault);
    this.planEditService.startPinEdit(PlanEditModeEnum.Clipping, {id: fault.id}, plan?.id);
    if (!this.deviceService.isTablet()) {
      this.changeService.setChanges(MmFaultRightPanePageEnum.plan);
    } else {
      this.changeService.changesPerMask.get(MmFaultRightPanePageEnum.plan)?.next(true);
    }
  }




}
